// external
import React, { FC, useEffect, useState, useMemo, useCallback, useRef } from 'react';

// internal
import './home.page.css';
import COEHeaderComponent from '../../components/coe-header/coe-header.component';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import COEDateRangeComponent from '../../components/coe-date-range/coe-date-range.component';
import { getMeasurementUnit } from '../../helpers/general.helpers';
import {
  getDefaultWeek,
  getIsoWeekApiDateRange,
  getIsoWeekDayDates,
  getIsoWeekDayLabels,
} from '../../helpers/datetime.helpers';
import { HOME_SCHEDULE_STORAGE_KEYS, getStoredValue, setStoredValue } from '../../helpers/cache.helpers';
import {
  Button,
  CircularProgress,
  Divider,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import useService from '../../hooks/service.hook';
import TrainingsService from '../../services/trainings.service';
import COEEmptyState from '../../components/coe-empty-state/coe-empty-state.component';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import COETrainingCard from '../../components/coe-training-card-2/coe-training-card-2.component';
import TrainingModalV2 from '../../components/coe-training-modal-v2';
import COECreatePersonalTrainingModal from '../../components/coe-create-personal-training-modal.component.tsx/coe-create-personal-training-modal.componen';
import COECreateTrainingPickerModal from '../../components/coe-create-training-picker-modal/coe-create-training-picker-modal.component';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import TeamsService from '../../services/teams.service';
import GroupsService from '../../services/groups.service';
import UserService from '../../services/user.service';
import { FitnessDeviceConnection } from '../settings/settings.constants';
import COESkeletonComponent from '../../components/general/coe-skeleton/coe-skeleton.component';
import { palette } from '../../theme';
import OnboardingModal from '../../components/coe-onboarding-modal/OnboardingModal';
import { User } from '../../models/user.models';

dayjs.extend(isoWeek);

export interface HomePageProps {
  view?: string;
}

export interface TrainingCardTeam {
  id: number;
  name: string;
  color: string;
  totalAssignees?: number;
  totalRsvp?: number;
}

export interface TrainingCardGroup {
  id: number;
  name: string;
}

export interface TrainingCardItem {
  id: number;
  title: string;
  time: string;
  type: 'personal' | 'team' | 'group';
  viewerType: 'athlete' | 'coach' | 'guardian';
  team: TrainingCardTeam;
  location: string;
  group: TrainingCardGroup;
  complete?: boolean;
  totalCompletedDistance?: number;
  totalCompletedDuration?: number;
  totalPlannedDistance?: number;
  totalPlannedDuration?: number;
  isWatchUpload?: boolean;
  rsvpStatus?: boolean;
  unreadComments?: number;
  ward?: { name: string };
}

const HomePage: FC<HomePageProps> = () => {
  // hooks
  const [searchParams, setSearchParams] = useSearchParams();
  const trainingsService = useService(TrainingsService);
  const teamsService = useService(TeamsService);
  const groupsService = useService(GroupsService);
  const userService = useService(UserService);
  const user: User = JSON.parse(window.localStorage.getItem('user') as string);

  // states
  const [week, setWeek] = useState<Dayjs>(() => {
    const urlWeek = searchParams.get('week');
    const storedWeek = getStoredValue(HOME_SCHEDULE_STORAGE_KEYS.WEEK, '');

    if (urlWeek && dayjs(urlWeek).isValid()) {
      return dayjs(urlWeek);
    }

    if (storedWeek && dayjs(storedWeek).isValid()) {
      return dayjs(storedWeek);
    }

    return getDefaultWeek();
  });
  const [trainings, setTrainings] = useState<TrainingCardItem[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const initialTrainingId = searchParams.get('trainingId');
  const initialTrainingType = (searchParams.get('trainingType') as 'athlete' | 'coach' | 'guardian') || undefined;
  const [showSideview, setShowSideview] = useState<boolean>(() => !!initialTrainingId);
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [createMode, setCreateMode] = useState<'log' | 'plan' | null>(null);
  const [selectedTraining, setSelectedTraining] = useState<number | undefined>(() =>
    initialTrainingId ? Number(initialTrainingId) : undefined
  );
  const [selectedTrainingType, setSelectedTrainingType] = useState<'athlete' | 'coach' | 'guardian' | undefined>(
    () => initialTrainingType ?? undefined
  );
  const [selectedWardName, setSelectedWardName] = useState<string | undefined>(undefined);

  // ─── Onboarding modal ────────────────────────────────────────────────────
  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(
    () => window.localStorage.getItem('coelab_onboarding_pending') === 'true'
  );
  const onboardingAccentColor =
    window.localStorage.getItem('coelab_onboarding_role') === 'guardian' ? '#E06A10' : palette.primary.main;
  const handleOnboardingClose = () => {
    window.localStorage.removeItem('coelab_onboarding_pending');
    window.localStorage.removeItem('coelab_onboarding_role');
    setShowOnboardingModal(false);
  };
  const [teams, setTeams] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>(() => {
    const urlTeam = searchParams.get('team');
    const storedTeam = getStoredValue(HOME_SCHEDULE_STORAGE_KEYS.TEAM, 'all');
    return urlTeam || storedTeam;
  });
  const [selectedGroup, setSelectedGroup] = useState<string>(() => {
    const urlGroup = searchParams.get('group');
    const storedGroup = getStoredValue(HOME_SCHEDULE_STORAGE_KEYS.GROUP, 'all');
    return urlGroup || storedGroup;
  });
  const isInitialTeamEffect = useRef(true);
  const initialSelectedTeamRef = useRef(selectedTeam);
  const initialSelectedGroupRef = useRef(selectedGroup);
  const [summaryStats, setSummaryStats] = useState<{
    weekly: {
      totalSessions: number;
      totalCompletedSessions: number;
      totalDistance: number;
      totalCompletedDistance: number;
      totalDuration: number;
      totalCompletedDuration: number;
    };
    annual: {
      completedDistance: number;
      completedDuration: number;
    };
  } | null>(null);
  const [isSummaryStatsLoading, setIsSummaryStatsLoading] = useState<boolean>(true);

  const [groups, setGroups] = useState<TrainingCardGroup[]>([]);

  useEffect(() => {
    if (selectedTeam && selectedTeam !== 'all') {
      groupsService
        .getGroups(selectedTeam)
        .then((resp) => {
          const fetchedGroups = resp.data.data || [];
          setGroups(fetchedGroups);

          if (isInitialTeamEffect.current) {
            const restoredGroup = initialSelectedGroupRef.current;
            // Keep restored group only if it still exists for this team
            if (
              restoredGroup !== 'all' &&
              !fetchedGroups.some((group: TrainingCardGroup) => String(group.id) === String(restoredGroup))
            ) {
              setSelectedGroup('all');
              setStoredValue(HOME_SCHEDULE_STORAGE_KEYS.GROUP, 'all');
            }
            isInitialTeamEffect.current = false;
          } else {
            // User changed team — reset group filter
            setSelectedGroup('all');
          }
        })
        .catch(() => {
          setGroups([]);
          if (isInitialTeamEffect.current) {
            if (initialSelectedGroupRef.current !== 'all') {
              setSelectedGroup('all');
              setStoredValue(HOME_SCHEDULE_STORAGE_KEYS.GROUP, 'all');
            }
            isInitialTeamEffect.current = false;
          } else {
            setSelectedGroup('all');
          }
        });
    } else {
      setGroups([]);
      if (isInitialTeamEffect.current) {
        isInitialTeamEffect.current = false;
      } else {
        setSelectedGroup('all');
      }
    }
  }, [selectedTeam, groupsService]);

  // ─── Garmin sync ─────────────────────────────────────────────────────────
  const [isGarminConnected, setIsGarminConnected] = useState<boolean>(false);
  const [syncingDays, setSyncingDays] = useState<Set<string>>(new Set());
  const [syncSnackbar, setSyncSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'warning' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Memoize teams MenuItems to prevent unnecessary re-renders
  const teamsMenuItems = useMemo(
    () =>
      teams.map((team) => (
        <MenuItem key={team.id} value={team.id} style={{ fontSize: '13px', fontWeight: 'bold' }}>
          {team.name}
        </MenuItem>
      )),
    [teams]
  );

  // Memoize groups MenuItems to prevent unnecessary re-renders
  const groupsMenuItems = useMemo(
    () =>
      groups.map((group) => (
        <MenuItem key={group.id} value={group.id} style={{ fontSize: '13px', fontWeight: 'bold' }}>
          {group.name}
        </MenuItem>
      )),
    [groups]
  );

  // Memoize week days array to prevent recreation on every render
  const weekDays = useMemo(() => getIsoWeekDayLabels(week), [week]);

  // Map of day label → Dayjs object for date comparisons in the grid
  const weekDayDates = useMemo(() => getIsoWeekDayDates(week), [week]);

  // Memoize trainings grouped by day to avoid recalculating filter/map 7 times per render
  const trainingsByDay = useMemo(() => {
    const grouped: Record<string, TrainingCardItem[]> = {};
    weekDays.forEach((day) => {
      grouped[day] = trainings.filter((training) => dayjs(training.time).format('ddd MMM D') === day);
    });
    return grouped;
  }, [trainings, weekDays]);

  // Helper function to format duration from seconds to "H:MM hr" format
  const formatDurationHoursMinutes = (seconds: number): string => {
    if (!seconds) {
      return '0:00 hr';
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')} hr`;
  };

  const hasActiveFilter = selectedTeam !== 'all' || selectedGroup !== 'all';

  const handleClearFilters = () => {
    setSelectedTeam('all');
    setSelectedGroup('all');
  };

  useEffect(() => {
    // Fetch teams on mount and validate restored team filter
    teamsService.getTeams().then((resp) => {
      const fetchedTeams = resp.data.data || [];
      setTeams(fetchedTeams);

      const restoredTeam = initialSelectedTeamRef.current;
      if (
        restoredTeam !== 'all' &&
        !fetchedTeams.some((team: { id: number | string }) => String(team.id) === String(restoredTeam))
      ) {
        setSelectedTeam('all');
        setStoredValue(HOME_SCHEDULE_STORAGE_KEYS.TEAM, 'all');
      }
    });
  }, [teamsService]);

  useEffect(() => {
    userService.getRegisteredConnections().then((res) => {
      const garmin = res.data.data?.find((c: FitnessDeviceConnection) => c.id === 'garmin');
      setIsGarminConnected(garmin?.connected ?? false);
    });
  }, []);

  // Memoize training params to prevent unnecessary recalculation
  const _getTrainingParams = useMemo(() => {
    const { start, end } = getIsoWeekApiDateRange(week);
    const params: any = { startTime: start, endTime: end };
    if (selectedTeam && selectedTeam !== 'all') params.team = selectedTeam;
    if (selectedGroup && selectedGroup !== 'all') params.group = selectedGroup;
    return params;
  }, [week, selectedTeam, selectedGroup]);

  const getSchedule = useCallback(() => {
    setIsLoading(true);
    setIsError(false);
    trainingsService
      .getTrainings2(_getTrainingParams)
      .then((response) => {
        setIsLoading(false);
        setTrainings(response.data.data);
      })
      .catch(() => {
        setIsLoading(false);
        setIsError(true);
      });
  }, [trainingsService, _getTrainingParams]);

  const getSummaryStats = useCallback(() => {
    setIsSummaryStatsLoading(true);
    trainingsService
      .getTrainingSummaryStats(_getTrainingParams)
      .then((response) => {
        setSummaryStats(response.data.data);
        setIsSummaryStatsLoading(false);
      })
      .catch(() => {
        // Silently fail for summary stats - don't show error state
        console.warn('Failed to fetch summary stats');
        setIsSummaryStatsLoading(false);
      });
  }, [trainingsService, _getTrainingParams]);

  const handleGarminSync = useCallback(
    (day: string, formattedDate: string) => {
      setSyncingDays((prev) => new Set(prev).add(day));
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      trainingsService
        .resyncGarminDay(formattedDate, timezone)
        .then(() => {
          getSchedule();
          setSyncSnackbar({
            open: true,
            message: `Successfully synced ${formattedDate} with Garmin`,
            severity: 'success',
          });
        })
        .catch((err) => {
          const status = err?.response?.status;
          if (status === 404) {
            setSyncSnackbar({
              open: true,
              message: 'No Garmin connection found. Connect your Garmin account in Settings.',
              severity: 'warning',
            });
          } else if (status === 503) {
            setSyncSnackbar({
              open: true,
              message: 'Garmin is currently unavailable. Please try again later.',
              severity: 'error',
            });
          } else {
            setSyncSnackbar({
              open: true,
              message: err?.response?.data?.message || 'Failed to sync Garmin data. Please try again.',
              severity: 'error',
            });
          }
        })
        .finally(() => {
          setSyncingDays((prev) => {
            const next = new Set(prev);
            next.delete(day);
            return next;
          });
        });
    },
    [trainingsService, getSchedule]
  );

  // update search params and get trainings for that week
  useEffect(() => {
    // set new search params
    const params: any = { week: week.format('MM-DD-YYYY') };
    if (selectedTeam && selectedTeam !== 'all') params.team = selectedTeam;
    if (selectedGroup && selectedGroup !== 'all') params.group = selectedGroup;
    setSearchParams(new URLSearchParams(params));

    // Store values in localStorage for persistence
    setStoredValue(HOME_SCHEDULE_STORAGE_KEYS.WEEK, week.format('MM-DD-YYYY'));
    setStoredValue(HOME_SCHEDULE_STORAGE_KEYS.TEAM, selectedTeam);
    setStoredValue(HOME_SCHEDULE_STORAGE_KEYS.GROUP, selectedGroup);

    getSchedule();
    getSummaryStats();
  }, [week, selectedTeam, selectedGroup, getSchedule, getSummaryStats]);

  const onTrainingClicked = (id: number, type: 'athlete' | 'coach' | 'guardian', wardName?: string) => {
    setShowSideview(true);
    setSelectedTraining(id);
    setSelectedTrainingType(type);
    setSelectedWardName(wardName);
  };

  return (
    <div className={window.localStorage.getItem('sidedrawer') ? 'page-full' : 'page'} id="page">
      <COEHeaderComponent title="Schedule" params={`week=${week.format('MM-DD-YYYY')}`} />
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* White section: controls + stats */}
        <div style={{ padding: '20px 20px 16px', backgroundColor: 'white', flexShrink: 0 }}>
          {/* Control Row: date picker + filters on left, Today + New Workout on right */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
              flexShrink: 0,
            }}
          >
            {/* Left group: date picker + team/group filters */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <COEDateRangeComponent value={week} setValue={(val: any) => setWeek(val)} />

              {/* Team filter */}
              <FormControl size="small" variant="outlined" sx={{ minWidth: 130 }}>
                <Select
                  value={selectedTeam === 'all' ? 'all' : selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  displayEmpty
                  sx={{
                    height: 36,
                    borderRadius: 20,
                    fontSize: '13px',
                    fontWeight: 600,
                    backgroundColor: palette.background.paper,
                    '& .MuiSelect-select': {
                      padding: '6px 32px 6px 12px',
                      fontWeight: 600,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.primary.main,
                    },
                    '& .MuiSelect-icon': {
                      color: palette.primary.main,
                    },
                  }}
                  renderValue={(value) => {
                    const label =
                      value === 'all'
                        ? 'All Teams'
                        : teams.find((t) => String(t.id) === String(value))?.name ?? 'All Teams';
                    return (
                      <span>
                        <span style={{ color: palette.primary.main }}>● </span>
                        {label}
                      </span>
                    );
                  }}
                >
                  <MenuItem value="all" style={{ fontSize: '13px', fontWeight: 600 }}>
                    All Teams
                  </MenuItem>
                  {teamsMenuItems}
                </Select>
              </FormControl>

              {/* Group filter */}
              <FormControl size="small" variant="outlined" sx={{ minWidth: 130 }}>
                <Select
                  value={selectedGroup === 'all' ? 'all' : selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  displayEmpty
                  sx={{
                    height: 36,
                    borderRadius: 20,
                    fontSize: '13px',
                    fontWeight: 600,
                    backgroundColor: palette.background.paper,
                    '& .MuiSelect-select': {
                      padding: '6px 32px 6px 12px',
                      fontWeight: 600,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.primary.main,
                    },
                    '& .MuiSelect-icon': {
                      color: palette.primary.main,
                    },
                  }}
                  renderValue={(value) => {
                    if (value === 'all') return 'All Groups';
                    const group = groups.find((g) => String(g.id) === String(value));
                    return group ? group.name : 'All Groups';
                  }}
                >
                  <MenuItem value="all" style={{ fontSize: '13px', fontWeight: 600 }}>
                    All Groups
                  </MenuItem>
                  {groupsMenuItems}
                </Select>
              </FormControl>

              {hasActiveFilter && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={handleClearFilters}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    height: 36,
                    borderRadius: 20,
                    paddingX: 2,
                    borderColor: palette.grey[200],
                    color: palette.text.primary,
                    backgroundColor: palette.grey[200],
                    '&:hover': {
                      borderColor: palette.grey[300],
                      backgroundColor: palette.grey[300],
                    },
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>

            {/* Right group: Today + New Workout */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 36,
                  borderRadius: 20,
                  paddingX: 2,
                  borderColor: palette.divider,
                  color: palette.text.primary,
                  '&:hover': {
                    borderColor: palette.primary.main,
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
                onClick={() => setWeek(dayjs())}
              >
                Today
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 36,
                  borderRadius: 20,
                  paddingX: 2,
                }}
                onClick={(e) => setPickerAnchor(e.currentTarget)}
              >
                New Workout
              </Button>
            </div>
          </div>

          {/* Stats Bar */}
          {isSummaryStatsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, flexShrink: 0, padding: '12px 0' }}>
              <COESkeletonComponent />
            </div>
          ) : (
            summaryStats && (
              <div style={{ display: 'flex', alignItems: 'stretch', marginBottom: 16, flexShrink: 0 }}>
                {/* Sessions */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '0 16px 0 0',
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      color: palette.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      fontWeight: 600,
                      display: 'block',
                      marginBottom: 4,
                    }}
                  >
                    Sessions
                  </Typography>
                  <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1 }}>
                    <span style={{ color: palette.primary.main }}>{summaryStats.weekly.totalCompletedSessions}</span>
                    <span style={{ color: palette.text.secondary, fontWeight: 400, fontSize: '0.875rem' }}>
                      {' '}
                      / {summaryStats.weekly.totalSessions}
                    </span>
                  </Typography>
                </div>

                <Divider orientation="vertical" flexItem />

                {/* Distance */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '0 16px',
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      color: palette.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      fontWeight: 600,
                      display: 'block',
                      marginBottom: 4,
                    }}
                  >
                    Distance
                  </Typography>
                  <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1 }}>
                    <span style={{ color: palette.primary.main }}>
                      {summaryStats.weekly.totalCompletedDistance.toFixed(0)} {getMeasurementUnit()}
                    </span>
                    <span style={{ color: palette.text.secondary, fontWeight: 400, fontSize: '0.875rem' }}>
                      {' '}
                      / {summaryStats.weekly.totalDistance.toFixed(0)} {getMeasurementUnit()}
                    </span>
                  </Typography>
                </div>

                <Divider orientation="vertical" flexItem />

                {/* Duration */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '0 16px',
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      color: palette.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      fontWeight: 600,
                      display: 'block',
                      marginBottom: 4,
                    }}
                  >
                    Duration
                  </Typography>
                  <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1 }}>
                    <span style={{ color: palette.primary.main }}>
                      {formatDurationHoursMinutes(summaryStats.weekly.totalCompletedDuration)}
                    </span>
                    <span style={{ color: palette.text.secondary, fontWeight: 400, fontSize: '0.875rem' }}>
                      {' '}
                      / {formatDurationHoursMinutes(summaryStats.weekly.totalDuration)}
                    </span>
                  </Typography>
                </div>

                <Divider orientation="vertical" flexItem />

                {/* Annual */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '0 0 0 16px',
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      color: palette.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      fontWeight: 600,
                      display: 'block',
                      marginBottom: 4,
                    }}
                  >
                    Annual
                  </Typography>
                  <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1, color: palette.primary.main }}>
                    {formatDurationHoursMinutes(summaryStats.annual.completedDuration)}
                    <span style={{ color: palette.text.secondary, fontWeight: 400, fontSize: '0.875rem' }}>
                      {' '}
                      / {summaryStats.annual.completedDistance.toFixed(1)} {getMeasurementUnit()} total
                    </span>
                  </Typography>
                </div>
              </div>
            )
          )}
        </div>
        {/* end white section */}
        {/* Grey section: schedule grid */}
        <div
          style={{
            padding: 20,
            backgroundColor: '#F5F5F7',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isLoading && (
            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
              <CircularProgress style={{ height: 150, width: 150 }} />
            </div>
          )}
          {isError && !isLoading && (
            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
              <COEEmptyState
                title={'Error while fetching schedule'}
                icon={<ErrorOutlineIcon style={{ width: 100, height: 100 }} color="error" />}
                description={
                  'An error occured while fetching your trainings. Please try again. If the error persists please reach out to support.'
                }
                primaryActionTitle={'Try again'}
                primaryAction={getSchedule}
              />
            </div>
          )}
          {!isError && !isLoading && (
            <>
              {trainings.length === 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    zIndex: 1000,
                  }}
                >
                  <Typography variant="h6" color="textSecondary">
                    No trainings available for this week
                  </Typography>
                </div>
              )}
              <div className="schedule-grid-wrapper">
                <div className="schedule-grid">
                  {weekDays.map((day) => {
                    const isToday = dayjs().format('ddd MMM D') === day;
                    const dayDate = weekDayDates[day];
                    const isWithin2Weeks = Math.abs(dayDate.diff(dayjs(), 'day')) < 14;
                    const showSyncButton = isGarminConnected && isWithin2Weeks;
                    return (
                      <div key={day} className={`schedule-grid-item${isToday ? ' schedule-grid-item--today' : ''}`}>
                        <div className="schedule-grid-item-header">
                          <Typography
                            variant="caption"
                            align="center"
                            display="block"
                            style={{
                              color: isToday ? palette.primary.main : undefined,
                              fontWeight: isToday ? 700 : undefined,
                            }}
                            color={isToday ? undefined : 'textSecondary'}
                          >
                            {day.substring(0, 3).toUpperCase()}
                          </Typography>
                          <div className={`schedule-grid-item-date${isToday ? ' schedule-grid-item-date--today' : ''}`}>
                            <Typography
                              variant="body2"
                              align="center"
                              fontWeight="bold"
                              style={{
                                color: isToday ? palette.primary.contrastText : undefined,
                                lineHeight: 1,
                              }}
                            >
                              {day.split(' ')[2]}
                            </Typography>
                          </div>
                          {showSyncButton && (
                            <div className="schedule-grid-item-sync">
                              {syncingDays.has(day) ? (
                                <CircularProgress size={14} />
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={() => handleGarminSync(day, dayDate.format('YYYY-MM-DD'))}
                                  title="Re-sync Garmin"
                                >
                                  <SyncIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}
                            </div>
                          )}
                          {showSyncButton && (
                            <div className="schedule-grid-item-sync">
                              {syncingDays.has(day) ? (
                                <CircularProgress size={14} />
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={() => handleGarminSync(day, dayDate.format('YYYY-MM-DD'))}
                                  title="Re-sync Garmin"
                                >
                                  <SyncIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="schedule-grid-item-cards">
                          {trainingsByDay[day]?.map((training) => (
                            <COETrainingCard
                              key={training.id}
                              title={training.title}
                              time={training.time}
                              location={training.location}
                              type={training.type}
                              viewerType={training.viewerType}
                              team={training.team}
                              group={training.group}
                              complete={training.complete}
                              completedDistance={training.totalCompletedDistance}
                              completedDuration={training.totalCompletedDuration}
                              plannedDistance={training.totalPlannedDistance}
                              plannedDuration={training.totalPlannedDuration}
                              onClick={() => onTrainingClicked(training.id, training.viewerType, training.ward?.name)}
                              isWatchUpload={training.isWatchUpload}
                              rsvpStatus={training.rsvpStatus}
                              unreadCommentsCount={training.unreadComments}
                              athleteName={
                                training.ward?.name ??
                                (training.type === 'personal' ? `${user.firstName} ${user.lastName}`.trim() : undefined)
                              }
                              isSelected={showSideview && selectedTraining === training.id}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
        {/* end grey section */}
      </div>
      <TrainingModalV2
        onClose={() => setShowSideview(false)}
        open={showSideview}
        trainingId={selectedTraining}
        trainingType={selectedTrainingType}
        onTrainingUpdated={getSchedule}
        wardName={selectedWardName}
      />
      <COECreateTrainingPickerModal
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        onSelect={(mode) => {
          setPickerAnchor(null);
          setCreateMode(mode);
        }}
      />
      <COECreatePersonalTrainingModal
        onClose={() => setCreateMode(null)}
        open={createMode !== null}
        handleRefresh={getSchedule}
        mode={createMode ?? 'plan'}
      />
      <OnboardingModal
        open={showOnboardingModal}
        accentColor={onboardingAccentColor}
        onContinue={handleOnboardingClose}
        onSkip={handleOnboardingClose}
      />
      <Snackbar
        open={syncSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setSyncSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSyncSnackbar((prev) => ({ ...prev, open: false }))}
          severity={syncSnackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {syncSnackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default HomePage;
